/**
 * Vincent B961 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Dictionary Cleanup.
 *
 * Repasse la blacklist B960.d sur tous les termes existants du dictionnaire
 * (endpoint backend GET /jargon/cleanup/preview) et propose de les masquer
 * en un clic (POST /jargon/cleanup/reject). Soft-delete : les termes
 * flaggés passent en `status="rejected"` et sont invisibles de l'UI, mais
 * restent en DB pour être restaurés en cas de faux positif.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, SpinnerGap } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DictCleanupModal({ open, onClose, onCleaned, lang = "fr" }) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [totalScanned, setTotalScanned] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/jargon/cleanup/preview`, { withCredentials: true });
        const list = data?.candidates || [];
        setCandidates(list);
        setTotalScanned(data?.total_scanned || 0);
        // Pre-select all candidates by default (user can uncheck false positives).
        setSelected(new Set(list.map((c) => c.id)));
      } catch (e) {
        toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, lang]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  const doReject = async () => {
    if (selected.size === 0) return;
    setRejecting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/cleanup/reject`,
        { ids: [...selected] },
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `${data.rejected_count} terme(s) marqué(s) comme non pertinent(s). Ils sont masqués mais récupérables.`
          : `${data.rejected_count} term(s) marked as irrelevant. They're hidden but recoverable.`,
      );
      onCleaned?.();
      onClose?.();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setRejecting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="dict-cleanup-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <h2 className="text-base font-semibold text-zinc-900">
            {lang === "fr" ? "Nettoyer les termes non pertinents" : "Cleanup irrelevant terms"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="dict-cleanup-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-xs text-zinc-700">
          {lang === "fr" ? (
            <>
              Ces termes ont été détectés comme non pertinents par la blacklist B960.d
              (mots outils, prénoms, verbes conjugués isolés). Ils seront <strong>masqués</strong>{" "}
              (status = <code>rejected</code>) mais restent en base pour être restaurés en cas de faux positif.
              <br />
              <span className="text-zinc-500">
                {totalScanned}{" "}
                {lang === "fr" ? `terme(s) scanné(s), ${candidates.length} candidat(s) détecté(s).` : `term(s) scanned, ${candidates.length} candidate(s) detected.`}
              </span>
            </>
          ) : (
            <>
              These terms were detected as irrelevant by the B960.d blacklist. They will be{" "}
              <strong>hidden</strong> (status = <code>rejected</code>) but kept in DB for restoration.
              <br />
              <span className="text-zinc-500">{totalScanned} scanned, {candidates.length} candidates.</span>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              <SpinnerGap size={20} className="inline animate-spin mr-2" />
              {lang === "fr" ? "Analyse en cours…" : "Analyzing…"}
            </div>
          ) : candidates.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500" data-testid="dict-cleanup-empty">
              {lang === "fr"
                ? "🎉 Aucun terme non pertinent détecté ! Le dictionnaire est propre."
                : "🎉 No irrelevant term detected! The dictionary is clean."}
            </div>
          ) : (
            <>
              <div className="sticky top-0 bg-zinc-50 border-b border-zinc-200 px-3 py-2 flex items-center gap-2 text-xs text-zinc-700">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === candidates.length}
                  onChange={toggleAll}
                  data-testid="dict-cleanup-select-all"
                />
                <span className="flex-1">
                  {lang === "fr"
                    ? `${selected.size} / ${candidates.length} sélectionné(s)`
                    : `${selected.size} / ${candidates.length} selected`}
                </span>
              </div>
              <ul className="divide-y divide-zinc-100" data-testid="dict-cleanup-list">
                {candidates.map((c) => {
                  const isSel = selected.has(c.id);
                  return (
                    <li
                      key={c.id}
                      className={`px-3 py-2 flex items-start gap-2 cursor-pointer ${isSel ? "bg-rose-50" : "hover:bg-zinc-50"}`}
                      onClick={() => toggle(c.id)}
                      data-testid={`dict-cleanup-row-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(c.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                        data-testid={`dict-cleanup-checkbox-${c.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-zinc-900">{c.term}</div>
                        {/* Tâche 1464 — définition ENTIÈRE : on supprime sur
                            une vue tronquée à deux lignes. */}
                        {c.definition && (
                          <div className="text-xs text-zinc-500 whitespace-pre-line">{c.definition}</div>
                        )}
                        <div className="text-[10px] text-zinc-400 mt-0.5">
                          {c.category ? `${c.category} · ` : ""}
                          {c.usage_count ? `${c.usage_count} usages` : "0 usage"}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-zinc-200">
          <button
            onClick={onClose}
            disabled={rejecting}
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
            data-testid="dict-cleanup-cancel"
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            onClick={doReject}
            disabled={rejecting || selected.size === 0}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="dict-cleanup-confirm"
          >
            {rejecting
              ? (lang === "fr" ? "Traitement…" : "Processing…")
              : (lang === "fr"
                  ? `Masquer les ${selected.size} termes sélectionnés`
                  : `Hide ${selected.size} selected term(s)`)}
          </button>
        </div>
      </div>
    </div>
  );
}
